/**
 * Reusable "Add User" component.
 *
 * Renders an email-search autocomplete backed by the Backstage azure backend
 * plugin user-lookup route.
 * with an "Add" button. Once a user is selected and added, they appear in a
 * removable list below the search box.
 *
 * This component is designed to be used anywhere in the Backstage app — not
 * just in scaffolder forms.
 */
import { useEffect, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import type { AccountKindValue } from '@internal/datasphere-personas';
import {
  UserOption,
  UserOptionValue as SearchUser,
  isWrongAccountKind,
} from '../UserOption';

export interface UserEntry {
  email: string;
  displayName: string;
}

export interface AddUserListProps {
  /** Current list of added users. */
  users: UserEntry[];
  /** Called whenever the list changes (add or remove). */
  onChange: (users: UserEntry[]) => void;
  /** Label shown above the search box. */
  label?: string;
  /** Helper text beneath the search box. */
  helperText?: string;
  /** Whether the field is required (at least one user). */
  required?: boolean;
  /** Error messages to display. */
  rawErrors?: string[];
  /** The account kind this list needs; the other kind cannot be added. */
  accountKind?: AccountKindValue;
}

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
  },
  searchField: {
    flex: 1,
  },
  addButton: {
    marginBottom: theme.spacing(0.5),
    whiteSpace: 'nowrap',
  },
  chipList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
  },
  emptyText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: '0.8125rem',
  },
}));

export const AddUserList = ({
  users,
  onChange,
  label = 'Search user',
  helperText,
  required,
  rawErrors,
  accountKind,
}: AddUserListProps) => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<SearchUser[]>([]);
  const [loading, setLoading] = useState(false);

  const hasError = Boolean(rawErrors?.length);

  // Debounced search — mirrors logic in UserEmailSearchField
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  // Clear any pending debounce timer whenever it changes or on unmount, so a
  // scheduled search never fires against an unmounted component (which leaks
  // the timer and triggers state updates after teardown).
  useEffect(() => {
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [debounceTimer]);

  const handleInputChange = (_: any, newInput: string, reason: string) => {
    setInputValue(newInput);
    if (reason === 'clear') {
      if (debounceTimer) clearTimeout(debounceTimer);
      setSelectedUser(null);
      setOptions([]);
      return;
    }

    const trimmed = newInput.trim();
    if (trimmed.length < 2) {
      if (debounceTimer) clearTimeout(debounceTimer);
      setOptions([]);
      return;
    }

    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const azureBase = await discoveryApi.getBaseUrl('azure');
        const response = await fetchApi.fetch(
          `${azureBase}/users/search?q=${encodeURIComponent(trimmed)}`,
        );
        if (response.ok) {
          const results: SearchUser[] = await response.json();
          setOptions(results);
        } else {
          setOptions([]);
        }
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    setDebounceTimer(timer);
  };

  const handleAdd = () => {
    if (!selectedUser) return;
    // Prevent duplicates
    if (users.some(u => u.email === selectedUser.email)) {
      setSelectedUser(null);
      setInputValue('');
      return;
    }
    onChange([
      ...users,
      { email: selectedUser.email, displayName: selectedUser.displayName },
    ]);
    setSelectedUser(null);
    setInputValue('');
    setOptions([]);
  };

  const handleRemove = (email: string) => {
    onChange(users.filter(u => u.email !== email));
  };

  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Enter' && selectedUser) {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box>
      <Box className={classes.row}>
        <Autocomplete
          className={classes.searchField}
          options={options}
          loading={loading}
          value={selectedUser}
          inputValue={inputValue}
          getOptionLabel={option => option.email}
          getOptionSelected={(option, selected) =>
            option.email === selected.email
          }
          getOptionDisabled={option => isWrongAccountKind(option, accountKind)}
          filterOptions={x => x}
          noOptionsText={
            inputValue.trim().length < 2
              ? 'Type 2+ characters to search'
              : 'No users found'
          }
          onChange={(_, newValue: SearchUser | null) => {
            setSelectedUser(newValue);
          }}
          onInputChange={handleInputChange}
          renderOption={option => (
            <UserOption user={option} accountKind={accountKind} />
          )}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              required={required && users.length === 0}
              error={hasError}
              helperText={hasError ? rawErrors?.join(', ') : helperText}
              margin="dense"
              onKeyDown={handleKeyDown}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          className={classes.addButton}
          variant="outlined"
          color="primary"
          size="small"
          disabled={!selectedUser}
          onClick={handleAdd}
        >
          Add
        </Button>
      </Box>

      {users.length > 0 ? (
        <Box className={classes.chipList}>
          {users.map(user => (
            <Chip
              key={user.email}
              label={`${user.displayName} (${user.email})`}
              onDelete={() => handleRemove(user.email)}
              variant="outlined"
              size="small"
            />
          ))}
        </Box>
      ) : (
        <Typography className={classes.emptyText}>
          No users added yet.
        </Typography>
      )}
    </Box>
  );
};
